import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  FormHelperText,
  IconButton,
  TextField,
} from "@mui/material";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import { Commodity } from "@/pages/sy/models/Commodity";
import { Autocomplete } from "@mui/material";

export type NewLayerMaterial = {
  commodityId: number;
  weight: number;
};

type MaterialDraft = {
  key: number;
  commodityId: number | null;
  weightInput: string;
};

interface RecipeLayerModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (materials: NewLayerMaterial[], chargeNumber: number) => void;
  commodities: Commodity[];
  excludedCommodityIds?: number[];
  isLoading: boolean;
  isSaving?: boolean;
  title: string;
  recipeId: number | null;
  chargeNumber?: number;
}

const createDraft = (key: number): MaterialDraft => ({
  key,
  commodityId: null,
  weightInput: "",
});

const parseWeight = (input: string) => {
  const value = parseFloat(input);
  return isNaN(value) ? 0 : value;
};

export const RecipeLayerModal: React.FC<RecipeLayerModalProps> = ({
  open,
  onClose,
  onSave,
  commodities,
  excludedCommodityIds = [],
  isLoading,
  isSaving = false,
  title,
  chargeNumber = 1,
}) => {
  const [drafts, setDrafts] = useState<MaterialDraft[]>([createDraft(0)]);
  const [showError, setShowError] = useState<boolean>(false);
  const nextDraftKey = useRef(1);

  const enabledCommodities = commodities.filter((c) => c.IsEnable);

  useEffect(() => {
    if (open) {
      setDrafts([createDraft(0)]);
      nextDraftKey.current = 1;
      setShowError(false);
    }
  }, [open]);

  const isAvailable = (commodity: Commodity, ownDraftKey?: number) =>
    commodity.CommodityId != null &&
    !excludedCommodityIds.includes(commodity.CommodityId) &&
    !drafts.some(
      (d) => d.key !== ownDraftKey && d.commodityId === commodity.CommodityId,
    );

  const getOptionsFor = (draftKey: number) =>
    enabledCommodities.filter((c) => isAvailable(c, draftKey));

  const canAddMaterial = enabledCommodities.some((c) => isAvailable(c));

  const updateDraft = (key: number, changes: Partial<MaterialDraft>) =>
    setDrafts((prev) =>
      prev.map((d) => (d.key === key ? { ...d, ...changes } : d)),
    );

  const addDraft = () => {
    setDrafts((prev) => [...prev, createDraft(nextDraftKey.current)]);
    nextDraftKey.current += 1;
  };

  const removeDraft = (key: number) =>
    setDrafts((prev) => prev.filter((d) => d.key !== key));

  const handleSave = () => {
    if (drafts.some((d) => !d.commodityId)) {
      setShowError(true);
      return;
    }

    onSave(
      drafts.map((d) => ({
        commodityId: d.commodityId as number,
        weight: parseWeight(d.weightInput),
      })),
      chargeNumber,
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: "30%",
            maxWidth: "700px",
            borderRadius: "12px",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: (theme) => theme.palette.primary.main,
          color: "white",
          padding: (theme) => theme.spacing(2),
          "& .MuiTypography-root": {
            fontSize: "1.5rem",
            fontWeight: 600,
          },
        }}
      >
        {title}
      </DialogTitle>

      <DialogContent
        sx={{
          padding: (theme) => theme.spacing(3),
          "&.MuiDialogContent-root": {
            paddingTop: (theme) => theme.spacing(3),
          },
          display: "flex",
          flexDirection: "column",
          gap: (theme) => theme.spacing(2),
        }}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <>
            {drafts.map((draft) => {
              const options = getOptionsFor(draft.key);
              const hasError = showError && !draft.commodityId;
              return (
                <Box
                  key={draft.key}
                  sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                >
                  <Box sx={{ flex: 2 }}>
                    <Autocomplete
                      options={options}
                      getOptionLabel={(option) => option.CommodityName || ""}
                      value={
                        options.find(
                          (c) => c.CommodityId === draft.commodityId,
                        ) ?? null
                      }
                      onChange={(_, newValue) =>
                        updateDraft(draft.key, {
                          commodityId: newValue ? newValue.CommodityId : null,
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Commodity"
                          variant="outlined"
                          size="small"
                          fullWidth
                          required
                          error={hasError}
                        />
                      )}
                    />
                    {hasError && (
                      <FormHelperText
                        sx={{
                          color: (theme) => theme.palette.error.main,
                          marginTop: (theme) => theme.spacing(1),
                        }}
                      >
                        Commodity selection is required
                      </FormHelperText>
                    )}
                  </Box>

                  <TextField
                    label="Weight"
                    variant="outlined"
                    value={draft.weightInput}
                    onChange={(e) =>
                      updateDraft(draft.key, { weightInput: e.target.value })
                    }
                    onBlur={() => {
                      if (draft.weightInput === "") {
                        updateDraft(draft.key, { weightInput: "0" });
                      }
                    }}
                    size="small"
                    type="number"
                    sx={{ flex: 1 }}
                    slotProps={{
                      htmlInput: {
                        min: 0,
                      },
                    }}
                  />

                  {drafts.length > 1 && (
                    <IconButton
                      onClick={() => removeDraft(draft.key)}
                      size="small"
                      sx={{ mt: 0.5 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              );
            })}

            <Button
              onClick={addDraft}
              disabled={!canAddMaterial || isSaving}
              startIcon={<AddIcon />}
              size="small"
              sx={{ alignSelf: "flex-start" }}
            >
              Add material
            </Button>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: (theme) => theme.spacing(2, 3, 3),
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: (theme) => theme.spacing(2),
            marginLeft: "auto",
          }}
        >
          <Button
            onClick={onClose}
            disabled={isLoading || isSaving}
            variant="contained"
            sx={{
              padding: (theme) => theme.spacing(1, 3),
              minWidth: "120px",
              borderRadius: "4px",
              backgroundColor: "#9e9e9e",
              color: "white",
              "&:hover": {
                backgroundColor: "#757575",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSave}
            disabled={
              isLoading || isSaving || drafts.some((d) => !d.commodityId)
            }
            variant="contained"
            sx={{
              padding: (theme) => theme.spacing(1, 3),
              minWidth: "120px",
              borderRadius: "4px",
              backgroundColor: (theme) => theme.palette.primary.main,
              color: "white",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.primary.dark,
              },
            }}
          >
            {isSaving ? <CircularProgress size={24} color="inherit" /> : "Save"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
